import React, { useState, useEffect } from "react"
import { Box, Stack, Typography, Button } from "@mui/material"
import Lottie from "react-lottie"
import countdownData from "assets/lottie/games/countdown.json"
import { useAuthContext } from "contexts/AuthContext"
import useSound from "hooks/useSound"
import { gamesSounds } from "utils/sounds"
import { useTranslation } from "react-i18next"
import { styles } from "../styles/StartGame.styles"
import { useLocation } from "react-router-dom"
import { prefixRoutes } from "common/navigationRoutes"

const lottieOptions = {
  loop: false,
  autoplay: true,
  animationData: countdownData,
  rendererSettings: {
    preserveAspectRatio: "xMidYMid slice",
  },
}

export default function StartGame({ game, onComplete }) {
  const { audioSettings } = useAuthContext()
  const [isPlaying, setIsPlaying] = useState(false)
  const [showGo, setShowGo] = useState(false)
  const { t } = useTranslation()
  const location = useLocation()
  const isEmbed = location.pathname.startsWith(prefixRoutes.EMBED)

  const [playStartSound] = useSound(gamesSounds.startGame1, {
    volume: 0.5,
    soundEnabled: audioSettings?.enabledAudioEffects,
  })

  const handleStart = React.useCallback(() => {
    setIsPlaying(true)
    playStartSound()
  }, [playStartSound])

  const handleCountdownComplete = React.useCallback(() => {
    setShowGo(true)
    setTimeout(() => {
      onComplete()
    }, 500)
  }, [onComplete])

  useEffect(() => {
    let timeoutId
    if (isPlaying && !showGo) {
      timeoutId = setTimeout(() => {
        handleCountdownComplete()
      }, 3500)
    }
    return () => {
      if (timeoutId) clearTimeout(timeoutId)
    }
  }, [isPlaying, showGo, handleCountdownComplete])

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!isPlaying && (event.key === "Enter" || event.key === " ")) {
        event.preventDefault()
        handleStart()
      }
    }

    window.addEventListener("keydown", handleKeyDown)
    return () => {
      window.removeEventListener("keydown", handleKeyDown)
    }
  }, [isPlaying, handleStart])

  const primaryColor = game?.color
  const secondaryColor = game?.colors?.[1] || game?.colors?.[0]

  return (
    <Box
      sx={[
        styles.fullContainer({ isEmbed }),
        {
          background: `linear-gradient(135deg, ${secondaryColor} 0%, ${primaryColor} 100%)`,
        },
      ]}
    >
      {!isPlaying ? (
        <Stack alignItems="center" spacing={3} sx={styles.content}>
          {game?.icon && (
            <Typography sx={styles.gameIcon}>{game.icon}</Typography>
          )}
          <Typography variant="h2" fontWeight={900} sx={styles.gameTitle}>
            {game?.title}
          </Typography>
          <Typography variant="h6" sx={styles.readyText}>
            {t("are_you_ready_click_start_to_begin")}
          </Typography>
          <Button
            onClick={handleStart}
            variant="contained"
            disableElevation
            sx={[
              styles.startButton,
              {
                backgroundColor: secondaryColor,
                "&:hover": {
                  color: secondaryColor,
                },
              },
            ]}
          >
            {t("start")}
          </Button>
        </Stack>
      ) : (
        <Box sx={styles.countdownContent}>
          {showGo ? (
            <Typography sx={[styles.goText, { color: primaryColor }]}>
              {t("Go!")}
            </Typography>
          ) : (
            <Lottie
              options={lottieOptions}
              eventListeners={[
                {
                  eventName: "complete",
                  callback: handleCountdownComplete,
                },
              ]}
              width={350}
              height={350}
            />
          )}
        </Box>
      )}
    </Box>
  )
}
